export type Pillar = Readonly<{
  name: string;
  short: string;
  detail: string;
  examples: readonly string[];
  /**
   * The pillar's own identifier, which is also its colour token.
   *
   * This used to be a hue name — 'cyan', 'lavender', 'navy' — chosen per card
   * on this site alone, while the employee app coloured the same seven areas
   * from its own palette keyed by pillar. Nobody reconciled them, so spiritual
   * was yellow in the app and lavender here, and occupational was pale blue
   * there and navy here. Naming the field after the concept rather than the
   * colour is what stops that happening again: `spiritual` survives a decision
   * to make spiritual green, `lavender` does not.
   */
  id: 'physical' | 'mental' | 'spiritual' | 'education' | 'financial' | 'occupational' | 'environmental' | 'challenges';
  symbol: string;
  /**
   * Shown instead of a sequence number.
   *
   * The section is headed "Seven dimensions", and the app's wellness hub shows
   * exactly seven tiles — challenges live inside each area rather than beside
   * them. Numbering Challenges "08" made the rail contradict its own heading
   * and the screenshot next to it, which is captioned "actual employee
   * experience". It is a real part of the product and keeps its card; it just
   * stops pretending to be an eighth area.
   */
  marker?: string;
  eyebrow?: string;
  dialogTitle?: string;
}>;

export const pillars: readonly Pillar[] = [
  {
    name: 'Physical',
    short: 'Move, recover, hydrate, and sleep.',
    detail: 'Physical wellness is the everyday care that helps your body meet the day you have.',
    examples: ['Movement that fits your day', 'Hydration habits', 'Rest and recovery'],
    id: 'physical',
    symbol: '↗',
  },
  {
    name: 'Mental',
    short: 'Make room for focus, calm, and support.',
    detail: 'Mental wellness includes simple tools for stress, focus, connection, and asking for help.',
    examples: ['A two-minute reset', 'Focus between tasks', 'Knowing where support begins'],
    id: 'mental',
    symbol: '◌',
  },
  {
    name: 'Spiritual',
    short: 'Connect daily choices to meaning.',
    detail: 'Spiritual wellness creates space for meaning, reflection, values, and a sense of belonging.',
    examples: ['A moment to reflect', 'Values in action', 'Connection and gratitude'],
    id: 'spiritual',
    symbol: '✦',
  },
  {
    name: 'Education',
    short: 'Learn something useful in minutes.',
    detail: 'Educational wellness builds confidence through clear lessons people can use in real life.',
    examples: ['Plain-language wellness lessons', 'Benefits education', 'Small skills that travel'],
    id: 'education',
    symbol: 'A',
  },
  {
    name: 'Financial',
    short: 'Turn money questions into clearer next steps.',
    detail: 'Financial wellness helps people understand everyday money choices and available resources.',
    examples: ['A simple spending check-in', 'Planning for the unexpected', 'Understanding available help'],
    id: 'financial',
    symbol: '$',
  },
  {
    name: 'Occupational',
    short: 'Support wellbeing through the workday.',
    detail: 'Occupational wellness looks at sustainable routines, purpose, recovery, and connection at work.',
    examples: ['Reset between demanding tasks', 'Build a safer routine', 'Recognize progress'],
    id: 'occupational',
    symbol: '⌁',
  },
  {
    name: 'Environmental',
    short: 'Shape spaces that make healthier choices easier.',
    detail: 'Environmental wellness considers how the spaces around us affect comfort, access, and routine.',
    examples: ['A calmer workspace', 'Everyday access and safety', 'Small sustainable choices'],
    id: 'environmental',
    symbol: '◎',
  },
  {
    name: 'Challenges',
    marker: 'Every month',
    short: 'Turn each month into approachable action.',
    detail: 'Monthly challenges bring the wellness dimensions to life through small actions, shared momentum, and practical education.',
    examples: ['A hydration reset', 'Five days of better sleep', 'A financial confidence week'],
    id: 'challenges',
    symbol: '✓',
    eyebrow: 'Put wellness into practice',
    dialogTitle: 'Monthly challenges',
  },
] as const;

export const journeyDetails = {
  discover: {
    title: 'Start with the person',
    eyebrow: '01 · Discover',
    copy: 'The employee chooses where to begin. Wellness Frontier helps turn that choice into a clear, approachable next step.',
    points: ['Employee-controlled starting point', 'Plain-language guidance', 'No employer view into private questions'],
  },
  engage: {
    title: 'Make wellness feel relevant',
    eyebrow: '02 · Engage',
    copy: 'Seven wellness pillars create more ways to participate than a one-size-fits-all activity challenge.',
    points: ['Monthly challenges and education', 'A mix of practical wellness dimensions', 'Progress that is motivating, not clinical'],
  },
  understand: {
    title: 'See program momentum',
    eyebrow: '03 · Understand',
    copy: 'Employers see authorized aggregate participation and program activity, never an employee’s private health story.',
    points: ['Aggregate program reporting', 'Suppression and role rules remain in force', 'No private or personal employee data'],
  },
} as const;

/**
 * The group thread that plays over the support photograph.
 *
 * THESE ARE HUMAN TEXTS BETWEEN COLLEAGUES, NOT APP NOTIFICATIONS, and that is
 * a product constraint rather than a copy preference. `packages/messaging`
 * ships exactly three strings today, all of them about the sign-in code — the
 * product sends no engagement messages at all. A phone showing "you earned 40
 * points" would advertise a capability that does not exist. Two coworkers
 * talking about a rollout needs no capability to be true.
 *
 * The handles are the synthetic ones from `packages/ui/stories/social-copy`.
 * They are not invented for this page and they are certainly not pilot
 * employees: `apps/android/store-assets/listing/README.md` records why the
 * pilot's real people never appear in a public asset, and this is a public
 * asset.
 *
 * English only. The product is bilingual; the marketing site is not
 * (operator decision, 2026-09-05).
 */
export type ThreadMessage = Readonly<{
  from: string;
  text: string;
  /** HR speaking as the organisation, styled as the reply that turns the thread. */
  official?: boolean;
}>;

export const supportThread: readonly ThreadMessage[] = [
  { from: 'Marcus T.', text: 'Do we have another boring step challenge this month?' },
  { from: 'Dee W.', text: 'I hope not' },
  {
    from: 'HR Department',
    text: 'Welcome to Wellness Frontier! No more boring step challenges!',
    official: true,
  },
  // The two who complained answer HR back. Operator's line, 2026-09-06, with
  // one correction: "HR heroes does it again" -> "do", because a plural
  // subject takes a plural verb and a typo in a screenshot of a text message
  // reads as a typo on the website rather than as texting shorthand.
  { from: 'Marcus T.', text: 'HR heroes do it again' },
  { from: 'Dee W.', text: 'We love HR, thank you!' },
] as const;

/**
 * Organisations willing to be named publicly as customers.
 *
 * THIS LIST IS EMPTY AND THAT IS THE CORRECT STATE TODAY. The pilot
 * organisation has not agreed to be a public reference (operator, 2026-09-06),
 * and a customer's name and mark are theirs, not ours. Publishing either one
 * before they say yes is a claim we are not entitled to make, and it is the
 * kind of claim that costs the relationship it is trying to advertise.
 *
 * `consent` is required rather than optional on purpose. The failure this
 * guards against is not malice — it is somebody adding a logo in a hurry
 * because the section looked empty and it was obvious who the customer was.
 * Making the record of approval part of the shape means the question "who said
 * yes to this, and when?" has to be answered before the entry compiles, and
 * test/partners.test.mjs fails the build if the answer is left blank.
 *
 * The section renders nothing at all while this is empty. No placeholder
 * boxes, no "your logo here" — an empty logo wall advertises that nobody has
 * agreed, which is worse than not having the section.
 */
export type Partner = Readonly<{
  /** Exactly as the organisation writes it. */
  name: string;
  /** Path under /public/media/partners, supplied by them, used as they supply it. */
  logo: string;
  /** Optional one line about the deployment, only as far as consent extends. */
  note?: string;
  consent: Readonly<{
    /** Named person who approved public use of the name and mark. */
    approvedBy: string;
    /** ISO date of that approval. */
    approvedOn: string;
    /** Where the approval is recorded — email, contract clause, ticket. */
    recordedIn: string;
  }>;
}>;

export const partners: readonly Partner[] = [];

/**
 * Pricing. Operator-supplied, 2026-09-06.
 *
 * VOLUME, NOT GRADUATED: the whole headcount is billed at the rate its band
 * qualifies for, rather than each band being charged separately. That is the
 * ordinary reading of a per-seat table like this one, and it has a consequence
 * worth knowing rather than discovering — 1,000 employees costs $1,000 while
 * 1,001 costs $900.90, so adding one person lowers the bill at each boundary.
 * Common in volume pricing, and deliberate here.
 *
 * The $100 floor is what covers everyone below 100 employees: at $1 each, one
 * hundred people is exactly $100, so the minimum and the first band meet
 * without a step.
 */
export type PriceBand = Readonly<{
  /** Inclusive lower bound of the band. */
  from: number;
  /** Inclusive upper bound, or null for the open-ended top band. */
  to: number | null;
  /** Dollars per employee per month. */
  rate: number;
}>;

export const PRICING = {
  minimumMonthly: 100,
  bands: [
    { from: 100, to: 1000, rate: 1 },
    { from: 1001, to: 2000, rate: 0.9 },
    { from: 2001, to: 3000, rate: 0.85 },
    { from: 3001, to: 4000, rate: 0.8 },
    { from: 4001, to: null, rate: 0.75 },
  ] as readonly PriceBand[],
} as const;

/**
 * The per-employee rate a headcount qualifies for.
 *
 * The loop always returns, because the last band is open-ended — but that is a
 * property of the data, not of the type, so the fallback is a real rate rather
 * than an index the compiler cannot prove exists.
 */
export function rateFor(employees: number): number {
  // `highest` trails the last band examined, so the fallback is always the top
  // rate as configured rather than a copy of it that could go stale. The loop
  // returns on its own while the last band is open-ended, which
  // test/pricing.test.mjs asserts; this only carries an empty table.
  let highest: number = PRICING.minimumMonthly;
  for (const band of PRICING.bands) {
    highest = band.rate;
    if (band.to === null || employees <= band.to) return band.rate;
  }
  return highest;
}

/**
 * Monthly cost for a headcount, in dollars.
 *
 * Below the first band the rate still applies and the floor does the work, so
 * this needs no special case for small teams — twenty employees is $20 by the
 * rate and $100 by the minimum, and the minimum wins.
 */
export function monthlyPrice(employees: number): number {
  // `|| 0` does not catch Infinity, which is truthy — and a number input
  // accepts "1e400", so this quoted an infinite price until a test asked.
  // Anything that is not a finite count is treated as no count, and the floor
  // answers.
  const counted = Number.isFinite(employees) ? Math.max(0, Math.floor(employees)) : 0;
  return Math.max(PRICING.minimumMonthly, counted * rateFor(counted));
}
